import _isObject from 'lodash/isObject'
import _min from 'lodash/min'
import _max from 'lodash/max'
import _sum from 'lodash/sum'
import _isEmpty from 'lodash/isEmpty'
import CoursesService from '../../../services/courses'
import ProgramsService from '../../../services/programs'
import { capitalize } from '@/utils/generalUtils'
import { mergeResourceFieldColumns } from '@/utils/filters'
import useFeatureFlags from '@/composables/useFeatureFlags'
import { extractCourseCredits } from '@/utils/dataLabels'
import { SHOW_REQUISITES_SUMMED_CREDITS, CM_COURSE_SETS_EFFECTIVE_DATING, USE_CATALOG_FILTERS_FOR_REQUISITES } from '@/constants/featureFlags'

const ENTITY_ID_MAP = {
  courses: 'courseGroupId',
  programs: 'programGroupId',
  courseSets: 'courseSetGroupId',
  requisiteSets: 'requisiteSetGroupId',
}

const DEPRECATED_ENTITY_ID_MAP = {
  courses: 'deprecatedCourseGroupId',
  programs: 'deprecatedProgramGroupId',
}

const flattenIds = (values) => {
  return (values || []).reduce((acc, v) => {
    if (_isObject(v)) acc.push(...v.value)
    else acc.push(v)
    return acc
  }, [])
}

const extractEntitesRecursive = (rule) => {
  const entityIds = []
  if (_isObject(rule.value) && rule.value) {
    entityIds.push(...extractEntitesRecursive(rule.value))
  }
  if (rule.subRules && rule.subRules.length) {
    rule.subRules.forEach((subRule) => {
      entityIds.push(...extractEntitesRecursive(subRule))
    })
  }
  if (
    ['courses', 'programs', 'courseSets', 'requisiteSets'].includes(
      rule.condition
    )
  ) {
    const ids =
      rule.condition === 'programs'
        ? rule.values.map((v) => ({ id: v, condition: rule.condition }))
        : flattenIds(rule.values).reduce((acc, id) => {
            acc.push({ id, condition: rule.condition })
            return acc
          }, [])
    entityIds.push(...ids)
  }
  return entityIds
}

function kCombinations(set, k) {
  let i, j, combinations, head, tail

  if (k > set.length || k <= 0) {
    return []
  }

  if (k === set.length) {
    return [set]
  }

  if (k === 1) {
    combinations = []
    for (i = 0; i < set.length; i++) {
      combinations.push([set[i]])
    }
    return combinations
  }

  combinations = []
  for (i = 0; i < set.length - k + 1; i++) {
    head = set.slice(i, i + 1)
    tail = kCombinations(set.slice(i + 1), k - 1)
    for (j = 0; j < tail.length; j++) {
      combinations.push(head.concat(tail[j]))
    }
  }
  return combinations
}

export default function useRequisites({ $axios, $store, $localize = (val) => val }) {
  const { showRequisitesCredits, enableCourseSetEffectiveDating, useCatalogFilters } = useFeatureFlags({
    showRequisitesCredits: SHOW_REQUISITES_SUMMED_CREDITS,
    enableCourseSetEffectiveDating: CM_COURSE_SETS_EFFECTIVE_DATING,
    useCatalogFilters: USE_CATALOG_FILTERS_FOR_REQUISITES,
  })

  const programsApi = new ProgramsService($axios, $store)
  const coursesApi = new CoursesService($axios, $store)

  const coursePreviewTemplate = $store.state.settings.coursePreviewTemplate || {
    questions: {},
  }
  const additionalCourseColumns = Object.values(
    coursePreviewTemplate.questions
  ).map((q) => {
    if (q.custom) return `customFields.${q.dataKey}`
    else return q.dataKey
  })
  const programPreviewTemplate = $store.state.settings
    .programPreviewTemplate || { questions: {} }
  const additionalProgramColumns = Object.values(
    programPreviewTemplate.questions
  ).map((q) => {
    if (q.custom) return `customFields.${q.dataKey}`
    else return q.dataKey
  })

  const courseColumns = mergeResourceFieldColumns(
    [
      'name',
      'code',
      'subjectCode',
      'courseNumber',
      'longName',
      'description',
      'id',
      'deprecatedCourseGroupId',
      'customFields.catalogAttributes',
      'customFields.rawCourseId',
      'customFields.crseOfferNbr',
      showRequisitesCredits && 'credits',
    ].filter(Boolean),
    additionalCourseColumns
  )
  const programColumns = mergeResourceFieldColumns(
    [
      'name',
      'code',
      'transcriptDescription',
      'longName',
      'description',
      'deprecatedProgramGroupId',
    ],
    additionalProgramColumns
  )

  const toFetch = {
    courses: new Set(),
    programs: new Set(),
    courseSets: new Set(),
    requisiteSets: new Set(),
  }

  const relatedRequisites = {
    courses: {},
    programs: {},
    courseSets: {},
    requisiteSets: {},
  }

  function getPrograms(ids) {
    if (!ids.length) return Promise.resolve({ data: [] })

    return programsApi.getPrograms({
      programGroupIds: ids,
      columns: programColumns,
      filters: !useCatalogFilters?{}: undefined, // If Feature Flag is enabled, use catalog filters, otherwise ignore filters
    })
  }
  function getCourses(ids) {
    if (!ids.length) return Promise.resolve({ data: [] })

    return coursesApi.getCourses({
      courseGroupIds: ids,
      columns: courseColumns,
      filters: !useCatalogFilters?{}: undefined, // If Feature Flag is enabled, use catalog filters, otherwise ignore filters
    })
  }

  const fetchRequisiteData = async (value) => {
    function mapEntitesToType(data, type) {
      data.forEach((entity) => {
        const entityId = ENTITY_ID_MAP[type] || 'id'
        const deprecatedEntityId = DEPRECATED_ENTITY_ID_MAP[type]

        relatedRequisites[type][entity[entityId]] = entity
        if (deprecatedEntityId && entity[deprecatedEntityId]) {
          relatedRequisites[type][
            `deprecated_${entity[deprecatedEntityId]}`
          ] = entity
        }
      })
    }

    value.forEach((requisite) => {
      requisite.rules.forEach((rule) => {
        extractEntitesRecursive(rule).forEach(({ id, condition }) => {
          toFetch[condition].add(id)
        })
      })
    })

    const effectiveDatesRange =
      $store.getters['settings/effectiveDatesRangeAsString']

    const courseSetsResponse = Array.from(toFetch.courseSets).length
      ? await $axios.$get(`/cm/${$store.state.settings.school}/courseSets`,
        {
          params: {
            courseSetGroupIds: Array.from(toFetch.courseSets),
            ...enableCourseSetEffectiveDating && effectiveDatesRange ? { effectiveDatesRange } : {}
          },
        }
      )
      : {}
    const courseSets = Object.values(courseSetsResponse)

    courseSets.forEach((courseSet) => {
      const ids =
        (courseSet.type === 'dynamic'
          ? courseSet.dynamicCourseList
          : courseSet.courseList) || []

      ids.forEach((id) => {
        toFetch.courses.add(id)
      })
    })

    const requisiteSets = !_isEmpty(toFetch.requisiteSets)
      ? await $axios.$get(
          `/${$store.state.settings.school}/requisite-sets/?list=${
            Array.isArray(toFetch.requisiteSets)
              ? toFetch.requisiteSets
              : Array.from(toFetch.requisiteSets).join(',')
          }&effectiveDatesRange=${effectiveDatesRange}`
        )
      : []

    const [courses, programs] = await Promise.all([
      getCourses(Array.from(toFetch.courses)),
      getPrograms(Array.from(toFetch.programs)),
    ])

    mapEntitesToType(courses.data, 'courses')
    mapEntitesToType(programs.data, 'programs')
    mapEntitesToType(courseSets, 'courseSets')
    mapEntitesToType(requisiteSets, 'requisiteSets')

    return relatedRequisites
  }

  const calculateRuleCreditsRange = (rule, relatedRequisites) => {
    const coursesCredits = []
    rule.value.values.forEach((course) => {
      if (!course.value || (course.value && !Array.isArray(course.value))) {
        return
      }

      course.value.forEach((cId) => {
        if (rule.value.condition === 'courseSets') {
          const entitiy = relatedRequisites.courseSets[cId]
          const ids =
            (entitiy.type === 'dynamic'
              ? entitiy.dynamicCourseList
              : entitiy.courseList) || []

          ids.forEach((id) => {
            coursesCredits.push(
              extractCourseCredits(relatedRequisites.courses[id])
            )
          })
        } else {
          coursesCredits.push(
            extractCourseCredits(relatedRequisites.courses[cId])
          )
        }
      })
    })

    return calculateRuleConditionCreditsRange(coursesCredits, rule)
  }

  const calculateRuleConditionCreditsRange = (coursesCredits, rule) => {
    const coursesMins = []
    const coursesMaxs = []

    Object.values(coursesCredits).forEach((credit) => {
      coursesMins.push(credit.min)
      coursesMaxs.push(credit.max)
    })

    switch (rule.condition) {
      case 'anyOf':
        return { min: _min(coursesMins), max: _max(coursesMaxs) }
      case 'allOf':
        return { min: _sum(coursesMins), max: _sum(coursesMaxs) }
      case 'numberOf':
        return false
      case 'freeformText':
        return false
      case 'minimumGrade': {
        return { min: _sum(coursesMins), max: _sum(coursesMaxs) }
      }
      case 'averageGrade': {
        return { min: _sum(coursesMins), max: _sum(coursesMaxs) }
      }
      case 'completedAllOf':
        return { min: _sum(coursesMins), max: _sum(coursesMaxs) }
      case 'completedAnyOf':
        return { min: _min(coursesMins), max: _max(coursesMaxs) }
      case 'completedAtLeastXOf': {
        const numberOfEntties = rule.restriction || 0
        const restrictedResultsMin = kCombinations(coursesMins, numberOfEntties)
        const restrictedResultsMax = kCombinations(coursesMaxs, numberOfEntties)

        const lowestMin = _min(restrictedResultsMin.map((r) => _sum(r)))
        const highestMax = _max(restrictedResultsMax.map((r) => _sum(r)))

        return { min: lowestMin, max: highestMax }
      }
      case 'enrolledIn':
        return { min: _sum(coursesMins), max: _sum(coursesMaxs) }
      case 'completeVariableCoursesAndVariableCredits':
        return { min: rule.minCredits, max: rule.maxCredits }
      case 'minimumCredits':
        return { min: rule.credits, max: rule.credits }
      case 'minimumResidencyCredits':
        return { min: rule.credits, max: rule.credits }
      default:
        return false
    }
  }

  const getRequisitesCreditsRange = (relatedRequisites, rules) => {
    if (!showRequisitesCredits) return ''
    if (!relatedRequisites) return ''
    let totalMin = 0
    let totalMax = 0
    rules.forEach((rule) => {
      if (rule.value && rule.value.values && rule.value.values.length) {
        const { min: localMin, max: localMax } = calculateRuleCreditsRange(
          rule,
          relatedRequisites
        )
        const parsedLocalMin = parseFloat(localMin)
        const parsedLocalMax = parseFloat(localMax)
        if (!Number.isNaN(parsedLocalMin)) totalMin = totalMin + localMin
        if (!Number.isNaN(parsedLocalMax)) totalMax = totalMax + localMax
      }
      const subRules = rule.subRules || rule.rules
      if (subRules && subRules.length) {
        const { min: localMin, max: localMax } = getRequisitesCreditsRange(
          relatedRequisites,
          subRules
        )
        const parsedLocalMin = parseFloat(localMin)
        const parsedLocalMax = parseFloat(localMax)
        if (!Number.isNaN(parsedLocalMin)) totalMin = totalMin + localMin
        if (!Number.isNaN(parsedLocalMax)) totalMax = totalMax + localMax
      }
    })

    return {
      min: totalMin,
      max: totalMax,
    }
  }

  const getRequisitesCreditsRangeLabel = (relatedRequisites, rules) => {
    const { min, max } = getRequisitesCreditsRange(relatedRequisites, rules)
    const creditAbbr = capitalize($localize('credit', 'short'))
    if (!min && !max) return ``
    if (!max && min) return `(${min} ${creditAbbr}.)`
    if (min !== 0 && !min && max) return `(${max} ${creditAbbr}.)`
    return min === max ? `(${min} ${creditAbbr}.)` : `(${min} - ${max} ${creditAbbr}.)`
  }

  return {
    fetchRequisiteData,
    getPrograms,
    getCourses,
    calculateRuleCreditsRange,
    calculateRuleConditionCreditsRange,
    getRequisitesCreditsRange,
    getRequisitesCreditsRangeLabel,
  }
}
